import * as React from 'react';

import { Checkbox, Select } from '@rematter/ui-web';

import { AccountRecord } from '../../_graphql/queryAccountsByIds';
import { useTranslations } from '../../i18n/useTranslations';

import { AccountAddress } from '~/modules/trade/_graphql/queryAccountAddresses';

export type LocationFormValues = {
  shipToLocationId: string | null;
  billToLocationId: string | null;
  sameAsShip: boolean;
  shippingAddress: {
    addressType: string;
    streetAddress: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  } | null;
  billingAddress: {
    addressType: string;
    streetAddress: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  } | null;
};

type Props = {
  disabled?: boolean;
  selectedAccount: AccountRecord;
  formData: LocationFormValues;
  onFormDataChange: (
    updater:
      | LocationFormValues
      | ((prev: LocationFormValues) => LocationFormValues),
  ) => void;
};

export const SalesOrderTabCustomerInfoLocationForm: React.FC<Props> = ({
  disabled = false,
  selectedAccount,
  formData,
  onFormDataChange,
}) => {
  const t = useTranslations();

  // Memoized values
  const selectedAccountAddresses = React.useMemo(
    () => selectedAccount?.addresses ?? [],
    [selectedAccount?.addresses],
  );

  // Callbacks
  const formatAddressDisplay = React.useCallback(
    (address: Omit<AccountAddress, 'id' | 'account'>) => {
      const parts = [
        address.streetAddress,
        address.city,
        address.state,
        address.postalCode,
      ].filter(Boolean);
      return parts.join(', ') || 'Address';
    },
    [],
  );

  const shipToAddressOptions = React.useMemo(
    () =>
      selectedAccountAddresses?.map((address) => ({
        key: address.id,
        label: `${address.address.addressType}・${formatAddressDisplay(address.address)}`,
      })) ?? [],
    [selectedAccountAddresses, formatAddressDisplay],
  );

  const billToAddressOptions = React.useMemo(
    () =>
      selectedAccountAddresses?.map((address) => ({
        key: address.id,
        label: `${address.address.addressType}・${formatAddressDisplay(address.address)}`,
      })) ?? [],
    [selectedAccountAddresses, formatAddressDisplay],
  );

  const handleShipToAddressChange = React.useCallback(
    (addressId: string) => {
      const selectedAddress = selectedAccountAddresses.find(
        (addr) => addr.id === addressId,
      );
      if (selectedAddress) {
        const newShippingAddress = {
          addressType: selectedAddress.address.addressType,
          streetAddress: selectedAddress.address.streetAddress || '',
          city: selectedAddress.address.city || '',
          state: selectedAddress.address.state || '',
          postalCode: selectedAddress.address.postalCode || '',
          country: selectedAddress.address.country || '',
        };

        onFormDataChange((prev) => ({
          ...prev,
          shipToLocationId: addressId,
          shippingAddress: newShippingAddress,
          ...(prev.sameAsShip && {
            billToLocationId: addressId,
            billingAddress: newShippingAddress,
          }),
        }));
      }
    },
    [selectedAccountAddresses, onFormDataChange],
  );

  const handleBillToAddressChange = React.useCallback(
    (addressId: string) => {
      const selectedAddress = selectedAccountAddresses.find(
        (addr) => addr.id === addressId,
      );
      if (selectedAddress) {
        onFormDataChange((prev) => ({
          ...prev,
          billToLocationId: addressId,
          billingAddress: {
            addressType: selectedAddress.address.addressType,
            streetAddress: selectedAddress.address.streetAddress || '',
            city: selectedAddress.address.city || '',
            state: selectedAddress.address.state || '',
            postalCode: selectedAddress.address.postalCode || '',
            country: selectedAddress.address.country || '',
          },
        }));
      }
    },
    [selectedAccountAddresses, onFormDataChange],
  );

  const handleSameAsShipChange = React.useCallback(
    (checked: boolean) => {
      onFormDataChange((prev) => ({
        ...prev,
        sameAsShip: checked,
        ...(checked && {
          billToLocationId: prev.shipToLocationId,
          billingAddress: prev.shippingAddress,
        }),
      }));
    },
    [onFormDataChange],
  );

  return (
    <div className="@SalesOrderTabCustomerInfoLocationForm space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Select
          label={t.salesOrdersView.wizard.contractInfo.shipToLocation}
          options={shipToAddressOptions}
          value={formData.shipToLocationId}
          onChange={handleShipToAddressChange}
          placeholder={
            t.salesOrdersView.wizard.placeholders.selectShipToAddress
          }
          isRequired
          isDisabled={disabled}
        />

        <div className="space-y-2">
          <Select
            label={t.salesOrdersView.wizard.contractInfo.billToLocation}
            options={billToAddressOptions}
            value={
              formData.sameAsShip
                ? formData.shipToLocationId
                : formData.billToLocationId
            }
            onChange={handleBillToAddressChange}
            placeholder={
              t.salesOrdersView.wizard.placeholders.selectBillToAddress
            }
            isRequired
            isDisabled={formData.sameAsShip || disabled}
          />

          <Checkbox
            isSelected={formData.sameAsShip}
            onChange={handleSameAsShipChange}
            isDisabled={disabled}
          >
            {t.salesOrdersView.wizard.contractInfo.sameAsShip}
          </Checkbox>
        </div>
      </div>
    </div>
  );
};
